import { DownloadIcon } from "@chakra-ui/icons";
import { Box, Button, Flex, Heading, Progress, Text } from "@chakra-ui/react";
import { LocalDate } from "@js-joda/core";

import { dateFormatter } from "../../../shared/utils/date-formatter";
import { downloadCsvFileData, jsonToCSV } from "../../../shared/utils/csv-utils";
import TasksV2Filters, { usePatientTaskTemplatesQuery } from "./components/TasksV2Filters";
import { useTasksV2AiCapabilities } from "./tasksV2.ai";
import TasksV2Pagination from "./components/TasksV2Pagination";
import TasksV2Table from "./components/TasksV2Table";
import TasksV2TaskDrawer from "./components/TasksV2TaskDrawer";
import { MergedRow, useTasksV2Data } from "./hooks/useTasksV2Data";
import { TasksV2FiltersProvider, useTasksV2Filters } from "./hooks/useTasksV2Filters";

function formatFullName(
  p: { firstName: string; middleName?: string | null; lastName: string } | null | undefined
): string {
  if (!p) return "";
  return [p.firstName, p.middleName, p.lastName].filter(Boolean).join(" ");
}

function statusLabel(row: MergedRow): string {
  if (row.__kind !== "regular") return "Incompleted";
  switch (row.status) {
    case "COMPLETED":
      return "Completed";
    case "APPROVED":
      return "Approved";
    case "INCOMPLETED":
    default:
      return "Incompleted";
  }
}

function rowsToCsv(rows: MergedRow[]): string {
  const csvRows = rows.map((row) => ({
    Patient: formatFullName(row.patient),
    Caregiver: formatFullName(row.caregiver),
    "Task name": row.taskName ?? "",
    Contract: row.contract?.contractType?.name ?? "",
    "Created at": row.createdAt ? dateFormatter.toDateOrDateTime(row.createdAt) : "",
    "Created by":
      row.__kind === "regular" && row.createdByMember ? formatFullName(row.createdByMember) : "",
    "Scheduled date": row.scheduledDate ? dateFormatter.toDate(row.scheduledDate) : "",
    Status: statusLabel(row),
    "Clock in":
      row.__kind === "regular" && row.clockInTime ? dateFormatter.toTime(row.clockInTime) : "",
    "Clock out":
      row.__kind === "regular" && row.clockOutTime ? dateFormatter.toTime(row.clockOutTime) : "",
  }));
  return jsonToCSV(csvRows);
}

function TasksV2PageContent() {
  const { rows, totalCount, isPending, isFetching } = useTasksV2Data();
  const filters = useTasksV2Filters();
  const templates = usePatientTaskTemplatesQuery();

  // This tab tells the omnibar what its own filters are, while it is on screen.
  useTasksV2AiCapabilities({ filters, templates: templates.data });

  const showProgressBar = isFetching && !isPending;

  function handleExport() {
    const csv = rowsToCsv(rows);
    const today = dateFormatter.toDate(LocalDate.now());
    downloadCsvFileData(`tasks-${today}.csv`, csv);
  }

  return (
    <Box px={4} py={5}>
      <Flex align="flex-end" justify="space-between" mb={4}>
        <Box>
          <Heading fontWeight="semibold" size="lg">
            Tasks 2.0
          </Heading>
          <Text color="gray.500" fontSize="sm" mt={1}>
            {isPending
              ? "Loading tasks…"
              : `${totalCount} tasks · click a row to see its documents`}
          </Text>
        </Box>
        <Button
          bg="white"
          isDisabled={rows.length === 0}
          leftIcon={<DownloadIcon />}
          size="sm"
          variant="outline"
          onClick={handleExport}
        >
          Export CSV
        </Button>
      </Flex>
      <Box
        bg="white"
        borderColor="gray.200"
        borderRadius="xl"
        borderWidth={1}
        boxShadow="sm"
        overflow="hidden"
        position="relative"
      >
        <TasksV2Filters />
        <Box position="relative">
          {showProgressBar && (
            <Progress
              isIndeterminate
              left={0}
              position="absolute"
              right={0}
              size="xs"
              top={0}
              zIndex={1}
            />
          )}
          <TasksV2Table />
        </Box>
        {!isPending && rows.length > 0 && <TasksV2Pagination />}
      </Box>
      <TasksV2TaskDrawer />
    </Box>
  );
}

export default function TasksV2Page() {
  return (
    <TasksV2FiltersProvider>
      <TasksV2PageContent />
    </TasksV2FiltersProvider>
  );
}
